/**
 * Arabic → Latin romanization in three systems.
 *
 * Sources, each read in full:
 *
 * - ALA-LC — the Library of Congress "Arabic Romanization Table", 2012 version
 *   (loc.gov/catdir/cpso/romanization/arabic.pdf): the letter table, the vowel signs and the
 *   26 rules of application. Hamza ʼ (U+02BC) and ayn ʻ (U+02BB) follow the MARC-8 characters
 *   the Library of Congress catalogues with; the prime is U+02B9.
 * - DMG / DIN 31635 — the Deutsche Morgenländische Gesellschaft's "Supplement to the
 *   transliteration rules … for the Arabic script" (dmg-web.de, 2025), which applies
 *   DIN 31635 to English as well as German writing: one symbol per phoneme (ṯ ǧ ḫ ḏ š ġ),
 *   hamzat al-qaṭʿ written ʾ throughout, the article assimilated before sun letters
 *   (aš-šams), diphthongs always aw and ay, alif maqṣūra always ā, tāʾ marbūṭa written t in
 *   the construct state and -a otherwise, and pausal forms in the body of a text. DIN 31635
 *   itself is a paid standard; its letter values were checked against the DMG supplement
 *   and the standard's published summaries.
 * - Simple — an ASCII-friendly romanization of the kind English-language news and
 *   dictionaries print: digraphs for the sibilants and fricatives, no dots or macrons, ʻ for
 *   ayn and ’ for hamza.
 *
 * What a program cannot do: Arabic is normally written without its short vowels, and the
 * vowels cannot be recovered from the consonants alone. Short vowels are written only when
 * the text carries vowel marks; in unvocalised text و and ي are read as the long vowels ū and
 * ī (kitāb, not kitab), so a diphthong such as bayt comes out as bīt.
 */

export type ArabicSystem = "ala-lc" | "din" | "simple";
export type TaMarbuta = "default" | "t";

export interface ArabicOptions {
  /** Drop final short vowels and tanwīn (ALA-LC rule 14, DMG rule 10). */
  pausal: boolean;
  /** `t` forces tāʾ marbūṭa to t, as in the construct state. */
  taMarbuta: TaMarbuta;
  capitalize: boolean;
}

export interface ArabicResult {
  text: string;
  /** Arabic-script words with three or more letters and no vowel mark at all. */
  unvocalised: number;
  /** Arabic-script words read. */
  words: number;
}

type Short = "" | "a" | "u" | "i";

interface Cluster {
  ch: string;
  vowel: Short;
  sukun: boolean;
  shadda: boolean;
  tanwin: Short;
  dagger: boolean;
  maddah: boolean;
  hamza: boolean;
  hamzaBelow: boolean;
}

type Role =
  | "cons"
  | "hamza"
  | "hamza-alif"
  | "carrier"
  | "alif-init"
  | "madda"
  | "wasla"
  | "long-a"
  | "long-u"
  | "long-i"
  | "w"
  | "y"
  | "diph-w"
  | "diph-y"
  | "maqsura"
  | "silent"
  | "tanwin-seat"
  | "ta";

const SYSTEM_INDEX: Record<ArabicSystem, number> = {
  "ala-lc": 0,
  din: 1,
  simple: 2,
};

/** Consonant values: [ALA-LC, DMG/DIN 31635, Simple]. */
const CONSONANTS: Record<string, readonly [string, string, string]> = {
  ب: ["b", "b", "b"],
  ت: ["t", "t", "t"],
  ث: ["th", "ṯ", "th"],
  ج: ["j", "ǧ", "j"],
  ح: ["ḥ", "ḥ", "h"],
  خ: ["kh", "ḫ", "kh"],
  د: ["d", "d", "d"],
  ذ: ["dh", "ḏ", "dh"],
  ر: ["r", "r", "r"],
  ز: ["z", "z", "z"],
  س: ["s", "s", "s"],
  ش: ["sh", "š", "sh"],
  ص: ["ṣ", "ṣ", "s"],
  ض: ["ḍ", "ḍ", "d"],
  ط: ["ṭ", "ṭ", "t"],
  ظ: ["ẓ", "ẓ", "z"],
  ع: ["ʻ", "ʿ", "‘"],
  غ: ["gh", "ġ", "gh"],
  ف: ["f", "f", "f"],
  ق: ["q", "q", "q"],
  ك: ["k", "k", "k"],
  ل: ["l", "l", "l"],
  م: ["m", "m", "m"],
  ن: ["n", "n", "n"],
  ه: ["h", "h", "h"],
  گ: ["g", "g", "g"],
  چ: ["ch", "č", "ch"],
  ژ: ["zh", "ž", "zh"],
  پ: ["p", "p", "p"],
  ڤ: ["v", "v", "v"],
  ڥ: ["v", "v", "v"],
  ۋ: ["v", "v", "v"],
  ڴ: ["ñ", "ñ", "ny"],
  ڢ: ["f", "f", "f"],
  ڧ: ["q", "q", "q"],
};

/** Hamza: [ALA-LC, DMG, Simple]. */
const HAMZA: readonly [string, string, string] = ["ʼ", "ʾ", "’"];

/** Letters that other letters stand in for. */
const LETTER_ALIAS: Record<string, string> = {
  ک: "ك",
  ی: "ي",
  ې: "ي",
  ہ: "ه",
  ھ: "ه",
  ە: "ه",
  ۃ: "ة",
  ٸ: "ء",
};

const SUN_LETTERS = new Set([..."تثدذرزسشصضطظلن"]);
const PRIME = "ʹ";
const DIGRAPH_HEADS = new Set([..."tskdg"]);

const ARABIC_RUN = /[ء-ٟٮ-ۓە-ۜ۟-۪ۨ-ۯۺ-ۿﭐ-ﴽﵐ-ﷻﹰ-ﻼ]+/g;
const PRESENTATION_FORMS = /[ﭐ-﷿ﹰ-﻿]/g;
const NON_SPACING = /[ً-ٰٟۖ-ۭـ]/g;
const COMBINING = /[̀-ͯ]/g;
const RE_ALIF_FORMS = /[أإآٱ]/g;
const RE_MAQSURA = /ى/g;
const FIRST_LETTER = /\p{L}/u;
const WORD_SPLIT = /(\s+)/;

const PUNCTUATION: Record<string, string> = {
  "،": ",",
  "؛": ";",
  "؟": "?",
  "٫": ",",
  "٬": ",",
};
const NON_RUN_CHARS = /[،؛؟٫٬٠-٩۰-۹]/g;

/** Words the rules of application single out (ALA-LC rules 23–26, DMG rule 8). */
const SPECIAL_WORDS: Record<string, readonly [string, string, string]> = {
  الله: ["Allāh", "Allāh", "Allah"],
  ابن: ["ibn", "ibn", "ibn"],
  بن: ["ibn", "ibn", "bin"],
  عمرو: ["ʻAmr", "ʿAmr", "‘Amr"],
  مائة: ["miʼah", "miʾa", "mi’a"],
  ذلك: ["dhālika", "ḏālika", "dhalika"],
  هذا: ["hādhā", "hāḏā", "hadha"],
  هذه: ["hādhihi", "hāḏihi", "hadhihi"],
  الى: ["ilá", "ilā", "ila"],
  الذي: ["alladhī", "allaḏī", "alladhi"],
  التي: ["allatī", "allatī", "allati"],
  الذين: ["alladhīna", "allaḏīna", "alladhina"],
  لله: ["lillāh", "li-llāh", "lillah"],
  طه: ["Ṭāhā", "Ṭāhā", "Taha"],
};

const SHORT_BY_MARK: Record<string, Short> = {
  "َ": "a",
  "ُ": "u",
  "ِ": "i",
};
const TANWIN_BY_MARK: Record<string, Short> = {
  "ً": "a",
  "ٌ": "u",
  "ٍ": "i",
};

function emptyCluster(ch: string): Cluster {
  return {
    ch,
    vowel: "",
    sukun: false,
    shadda: false,
    tanwin: "",
    dagger: false,
    maddah: false,
    hamza: false,
    hamzaBelow: false,
  };
}

function applyMark(cluster: Cluster, mark: string): void {
  if (mark in SHORT_BY_MARK) {
    cluster.vowel = SHORT_BY_MARK[mark] ?? "";
  } else if (mark in TANWIN_BY_MARK) {
    cluster.tanwin = TANWIN_BY_MARK[mark] ?? "";
  } else if (mark === "ّ") {
    cluster.shadda = true;
  } else if (mark === "ْ") {
    cluster.sukun = true;
  } else if (mark === "ٓ") {
    cluster.maddah = true;
  } else if (mark === "ٔ") {
    cluster.hamza = true;
  } else if (mark === "ٕ") {
    cluster.hamzaBelow = true;
  } else if (mark === "ٰ") {
    cluster.dagger = true;
  }
}

/** Split one Arabic-script word into letters, each with its vowel signs. */
function parseWord(word: string): Cluster[] {
  const clusters: Cluster[] = [];
  const decomposed = word
    .replace(PRESENTATION_FORMS, (c) => c.normalize("NFKC"))
    .normalize("NFD");
  for (const ch of decomposed) {
    const code = ch.codePointAt(0) ?? 0;
    const isMark = (code >= 0x6_4b && code <= 0x6_5f) || code === 0x6_70;
    const last = clusters.at(-1);
    if (isMark && last) {
      applyMark(last, ch);
    } else if (
      !(isMark || code === 0x6_40 || (code >= 0x6_d6 && code <= 0x6_ed))
    ) {
      clusters.push(emptyCluster(LETTER_ALIAS[ch] ?? ch));
    }
  }
  // A dotless final yeh is the alif maqṣūra only at the end of a word.
  return clusters.map((cluster, index) =>
    cluster.ch === "ى" && index < clusters.length - 1
      ? { ...cluster, ch: "ي" }
      : cluster
  );
}

let specialIndex: Map<string, readonly [string, string, string]> | undefined;

/** SPECIAL_WORDS keyed by their consonant skeleton, so vowel marks and hamza seats do not matter. */
function specialWords(): Map<string, readonly [string, string, string]> {
  if (!specialIndex) {
    specialIndex = new Map(
      Object.entries(SPECIAL_WORDS).map(([key, value]) => [
        skeleton(key),
        value,
      ])
    );
  }
  return specialIndex;
}

function skeleton(word: string): string {
  return word
    .normalize("NFKC")
    .replace(NON_SPACING, "")
    .normalize("NFD")
    .replace(COMBINING, "")
    .replace(RE_ALIF_FORMS, "ا")
    .replace(RE_MAQSURA, "ي");
}

function isLongRole(role: Role): boolean {
  return (
    role === "long-a" ||
    role === "long-u" ||
    role === "long-i" ||
    role === "maqsura"
  );
}

function alifRole(cs: readonly Cluster[], i: number): Role {
  const c = cs[i] as Cluster;
  const prev = cs[i - 1];
  if (c.maddah) {
    return "madda";
  }
  if (c.hamza || c.hamzaBelow) {
    return "hamza-alif";
  }
  if (c.ch === "ٱ") {
    return "wasla";
  }
  if (i === 0) {
    return "alif-init";
  }
  if (c.tanwin === "a") {
    return "tanwin-seat";
  }
  if (prev?.tanwin === "a") {
    return "silent";
  }
  if (
    prev?.ch === "و" &&
    !prev.vowel &&
    !prev.shadda &&
    i === cs.length - 1 &&
    roleAt(cs, i - 1) === "long-u"
  ) {
    return "silent";
  }
  return "long-a";
}

/** و or ي straight before an alif that is not the last letter is the consonant (khawārizmī, not khūārizmī). */
function beforeMedialAlif(cs: readonly Cluster[], i: number): boolean {
  const next = cs[i + 1];
  return next?.ch === "ا" && i + 1 < cs.length - 1;
}

function waw(cs: readonly Cluster[], i: number): Role {
  const c = cs[i] as Cluster;
  const prev = cs[i - 1];
  if (c.hamza) {
    return "carrier";
  }
  if (i === 0 || c.vowel || c.shadda || c.tanwin || !prev || prev.sukun) {
    return "w";
  }
  if (beforeMedialAlif(cs, i)) {
    return "w";
  }
  if (prev.vowel === "a" && !isLongRole(roleAt(cs, i - 1))) {
    return "diph-w";
  }
  if (prev.vowel === "i" || isLongRole(roleAt(cs, i - 1))) {
    return "w";
  }
  return "long-u";
}

function yaa(cs: readonly Cluster[], i: number): Role {
  const c = cs[i] as Cluster;
  const prev = cs[i - 1];
  if (c.hamza) {
    return "carrier";
  }
  if (i === 0 || c.vowel || c.shadda || c.tanwin || !prev || prev.sukun) {
    return "y";
  }
  if (beforeMedialAlif(cs, i)) {
    return "y";
  }
  if (prev.vowel === "a" && !isLongRole(roleAt(cs, i - 1))) {
    return "diph-y";
  }
  if (prev.vowel === "u" || isLongRole(roleAt(cs, i - 1))) {
    return "y";
  }
  return "long-i";
}

function maqsuraRole(cs: readonly Cluster[], i: number): Role {
  const c = cs[i] as Cluster;
  const prev = cs[i - 1];
  if (c.hamza) {
    return "carrier";
  }
  if (c.shadda || prev?.vowel === "i") {
    return "long-i";
  }
  return "maqsura";
}

function roleAt(cs: readonly Cluster[], i: number): Role {
  const c = cs[i];
  if (!c) {
    return "cons";
  }
  switch (c.ch) {
    case "ا":
    case "ٱ":
      return alifRole(cs, i);
    case "و":
      return waw(cs, i);
    case "ي":
      return yaa(cs, i);
    case "ى":
      return maqsuraRole(cs, i);
    case "ة":
      return "ta";
    case "ء":
      return "hamza";
    default:
      return "cons";
  }
}

/**
 * What a cluster's own short vowel turns into when the next letter is a long vowel.
 * `skip` — the long vowel letter prints it; `ū` / `ī` — a doubled و or ي after u / i, where
 * this cluster prints the long vowel and the doubled letter is written once.
 */
function absorbedBy(cs: readonly Cluster[], i: number): string {
  const c = cs[i] as Cluster;
  const next = cs[i + 1];
  if (!next || c.dagger) {
    return "";
  }
  const role = roleAt(cs, i + 1);
  if (role === "long-a" || role === "maqsura") {
    return c.vowel === "u" || c.vowel === "i" ? "" : "skip";
  }
  if (role === "long-u") {
    return c.vowel === "u" || !c.vowel ? "skip" : "";
  }
  if (role === "long-i") {
    return c.vowel === "i" || !c.vowel ? "skip" : "";
  }
  if (role === "w" && next.shadda && c.vowel === "u") {
    return "ū";
  }
  if (role === "y" && next.shadda && c.vowel === "i") {
    return "ī";
  }
  return "";
}

function plain(text: string, ctx: Ctx): string {
  return ctx.system === "simple"
    ? text.replace("ā", "a").replace("ū", "u").replace("ī", "i")
    : text;
}

const LONG_BASE: Record<string, string> = { ā: "a", ū: "u", ī: "i" };

function longVowel(role: Role, system: ArabicSystem, macron: string): string {
  if (system === "simple") {
    return LONG_BASE[macron] ?? macron;
  }
  if (role === "maqsura" && system === "ala-lc") {
    return "á";
  }
  return macron;
}

interface Ctx {
  system: ArabicSystem;
  options: ArabicOptions;
  pausalFrom: number;
}

function hamzaChar(ctx: Ctx): string {
  return HAMZA[SYSTEM_INDEX[ctx.system]] as string;
}

function consonantText(c: Cluster, ctx: Ctx): string {
  const entry = CONSONANTS[c.ch];
  return entry ? (entry[SYSTEM_INDEX[ctx.system]] as string) : c.ch;
}

function shortVowel(c: Cluster, ctx: Ctx, i: number): string {
  if (c.dagger) {
    return ctx.system === "simple" ? "a" : "ā";
  }
  let out = "";
  const pausal = ctx.options.pausal && i >= ctx.pausalFrom;
  if (c.vowel && !pausal) {
    out += c.vowel;
  }
  if (c.tanwin && !pausal) {
    out += `${c.tanwin}n`;
  }
  return out;
}

function appendConsonant(out: string, text: string, ctx: Ctx): string {
  const needsPrime =
    ctx.system !== "din" &&
    text.startsWith("h") &&
    DIGRAPH_HEADS.has(out.at(-1) ?? "");
  return needsPrime ? `${out}${PRIME}${text}` : out + text;
}

function emitTa(c: Cluster, ctx: Ctx, i: number, prevVowel: Short): string {
  const pausal = ctx.options.pausal && i >= ctx.pausalFrom;
  const shown = (c.vowel || c.tanwin) && !pausal;
  if (c.tanwin === "a") {
    return "tan";
  }
  if (ctx.options.taMarbuta === "t" || shown) {
    return `t${shortVowel(c, ctx, i)}`;
  }
  if (ctx.system === "ala-lc") {
    return "h";
  }
  // The a of -a is the fatḥah on the letter before, when it is written.
  return prevVowel === "a" ? "" : "a";
}

function emitHamza(
  c: Cluster,
  ctx: Ctx,
  initial: boolean,
  vowel: string
): string {
  const skip = initial && ctx.system !== "din";
  const out = skip ? "" : hamzaChar(ctx);
  if (initial && !vowel) {
    return out + (c.hamzaBelow ? "i" : "a");
  }
  return out + vowel;
}

/** و or ي as a consonant, including the doubled forms after u and i (ūw, īy, aww, ayy). */
function emitSemivowel(
  cs: readonly Cluster[],
  i: number,
  role: Role,
  vowel: string
): string {
  const c = cs[i] as Cluster;
  const base = role === "w" ? "w" : "y";
  const mergedBefore =
    i > 0 && ["ū", "ī"].includes(absorbedBy(cs, i - 1)) && c.shadda;
  if (mergedBefore && role === "y" && i === cs.length - 1 && !vowel) {
    return "";
  }
  const letters = c.shadda && !mergedBefore ? base + base : base;
  return letters + vowel;
}

/** One cluster's output, given the roles around it. */
function emitCluster(
  cs: readonly Cluster[],
  i: number,
  out: string,
  ctx: Ctx
): string {
  const c = cs[i] as Cluster;
  const role = roleAt(cs, i);
  const prev = cs[i - 1];
  const initial = i === 0;
  const merged = absorbedBy(cs, i);
  const vowel =
    merged === "skip" ? "" : plain(merged || shortVowel(c, ctx, i), ctx);
  switch (role) {
    case "cons":
      return appendConsonant(
        out,
        `${consonantText(c, ctx).repeat(c.shadda ? 2 : 1)}${vowel}`,
        ctx
      );
    case "hamza":
    case "hamza-alif":
    case "carrier":
      return out + emitHamza(c, ctx, initial, vowel);
    case "alif-init":
      return out + (shortVowel(c, ctx, i) || "a");
    case "wasla":
      return out + (shortVowel(c, ctx, i) || "i");
    case "madda":
      return (
        out + (initial ? "" : hamzaChar(ctx)) + longVowel(role, ctx.system, "ā")
      );
    case "long-a":
      return out + longVowel(role, ctx.system, "ā");
    case "maqsura":
      return out + longVowel(role, ctx.system, "ā");
    case "long-u":
      return out + longVowel(role, ctx.system, "ū");
    case "long-i":
      return out + longVowel(role, ctx.system, "ī");
    case "w":
    case "y":
      return out + emitSemivowel(cs, i, role, vowel);
    case "diph-w":
      return `${out}w`;
    case "diph-y":
      return `${out}y`;
    case "tanwin-seat":
      return out + (ctx.options.pausal ? "" : "an");
    case "ta":
      return out + emitTa(c, ctx, i, prev?.vowel ?? "");
    default:
      return out;
  }
}

function capitalizeWord(text: string): string {
  const match = FIRST_LETTER.exec(text);
  if (!match) {
    return text;
  }
  const at = match.index;
  return (
    text.slice(0, at) + (text[at] ?? "").toUpperCase() + text.slice(at + 1)
  );
}

function pausalStart(cs: readonly Cluster[]): number {
  let k = cs.length - 1;
  while (k > 0 && ["silent", "tanwin-seat"].includes(roleAt(cs, k))) {
    k -= 1;
  }
  if (cs.length < 3 || cs[k]?.ch === "ه") {
    return cs.length + 1;
  }
  return k;
}

function articleOf(cs: readonly Cluster[]): boolean {
  const [a, l] = cs;
  return (
    cs.length >= 3 &&
    !!a &&
    !!l &&
    (a.ch === "ا" || a.ch === "ٱ") &&
    !a.hamza &&
    !a.hamzaBelow &&
    !a.maddah &&
    l.ch === "ل" &&
    !l.vowel
  );
}

function romanizeClusters(cs: readonly Cluster[], ctx: Ctx): string {
  let out = "";
  for (let i = 0; i < cs.length; i += 1) {
    out = emitCluster(cs, i, out, ctx);
  }
  return out;
}

const CLITICS: Record<string, string> = { و: "w", ف: "f", ب: "b", ك: "k" };

/** The article, as each system writes it, with the clitic (wa-, bi-, …) that precedes it, if any. */
function articleText(sun: Cluster, ctx: Ctx, clitic: string): string {
  if (ctx.system === "din") {
    const assimilated = SUN_LETTERS.has(sun.ch);
    if (clitic) {
      return `${clitic}${assimilated ? consonantText(sun, ctx) : "l"}-`;
    }
    return assimilated ? `a${consonantText(sun, ctx)}-` : "al-";
  }
  return `${clitic}al-`;
}

function cliticOf(cs: readonly Cluster[]): string {
  const first = cs[0];
  if (!first || (first.vowel !== "a" && first.vowel !== "i")) {
    return "";
  }
  if (!(first.ch in CLITICS && articleOf(cs.slice(1)))) {
    return "";
  }
  return `${CLITICS[first.ch]}${first.vowel}-`;
}

function romanizeWord(word: string, ctx: Ctx, index: number): string {
  const { options } = ctx;
  const special = specialWords().get(skeleton(word));
  if (special) {
    const text = special[SYSTEM_INDEX[ctx.system]] as string;
    const isIbn = text.toLowerCase().endsWith("ibn") || text === "bin";
    return options.capitalize && (!isIbn || index === 0)
      ? capitalizeWord(text)
      : text;
  }
  let cs = parseWord(word);
  let prefix = "";
  const clitic = cliticOf(cs);
  const rest = clitic ? cs.slice(1) : cs;
  if (articleOf(rest)) {
    prefix = articleText(rest[2] as Cluster, ctx, clitic);
    cs = rest.slice(2).map((c, i) => (i === 0 ? { ...c, shadda: false } : c));
  }
  const body = romanizeClusters(cs, { ...ctx, pausalFrom: pausalStart(cs) });
  return prefix + (options.capitalize ? capitalizeWord(body) : body);
}

function hasVowelMark(cs: readonly Cluster[]): boolean {
  return cs.some((c) => c.vowel || c.sukun || c.tanwin || c.shadda || c.dagger);
}

/** Romanize Arabic-script text; anything that is not Arabic script passes through. */
export function romanizeArabic(
  input: string,
  system: ArabicSystem,
  options: ArabicOptions
): ArabicResult {
  let unvocalised = 0;
  let words = 0;
  const ctx: Ctx = { system, options, pausalFrom: 0 };
  const mapped = input.replace(NON_RUN_CHARS, (c) => {
    const code = c.codePointAt(0) ?? 0;
    if (c in PUNCTUATION) {
      return PUNCTUATION[c] ?? c;
    }
    return String(code >= 0x6_f0 ? code - 0x6_f0 : code - 0x6_60);
  });
  const pieces = mapped.split(WORD_SPLIT);
  const text = pieces
    .map((piece) =>
      piece.replace(ARABIC_RUN, (word) => {
        const cs = parseWord(word);
        words += 1;
        if (cs.length >= 3 && !hasVowelMark(cs)) {
          unvocalised += 1;
        }
        return romanizeWord(word, ctx, words - 1);
      })
    )
    .join("");
  return { text: text.normalize("NFC"), unvocalised, words };
}

export interface ArabicLetterRow {
  arabic: string;
  values: readonly [string, string, string];
}

const TABLE_LETTERS =
  "ب ت ث ج ح خ د ذ ر ز س ش ص ض ط ظ ع غ ف ق ك ل م ن ه گ چ ژ پ ڤ".split(" ");

/** The consonant table, plus the letters whose value depends on position. */
export function arabicLetterRows(): ArabicLetterRow[] {
  const rows: ArabicLetterRow[] = [{ arabic: "ء", values: ["ʼ", "ʾ", "’"] }];
  for (const letter of TABLE_LETTERS) {
    const entry = CONSONANTS[letter];
    if (entry) {
      rows.push({ arabic: letter, values: entry });
    }
  }
  rows.push(
    { arabic: "و", values: ["w, ū, aw", "w, ū, aw", "w, u, aw"] },
    { arabic: "ي", values: ["y, ī, ay", "y, ī, ay", "y, i, ay"] }
  );
  return rows;
}

export const ARABIC_SYSTEMS: readonly ArabicSystem[] = [
  "ala-lc",
  "din",
  "simple",
];
